/* Updated Project Modal with Company/Contact linking and Budget Hours */
import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { useData } from '../../contexts/DataContext';

const { FiX, FiBriefcase, FiUser, FiDollarSign, FiClock, FiActivity } = FiIcons;

const ProjectModal = ({ isOpen, onClose, project }) => {
  const { addProject, updateProject, contacts, companies } = useData();
  const [formData, setFormData] = useState({
    name: '', description: '', status: 'Planning', budget: '', budgetHours: '', deadline: '',
    billingType: 'Fixed', companyId: '', contactId: ''
  });

  useEffect(() => {
    if (project) setFormData(project);
    else setFormData({ name: '', description: '', status: 'Planning', budget: '', budgetHours: '', deadline: '', billingType: 'Fixed', companyId: '', contactId: '' });
  }, [project, isOpen]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (project) await updateProject(project.id, formData);
    else await addProject(formData);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm flex items-center justify-center p-4 z-[60]">
        <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="bg-white rounded-[2.5rem] shadow-2xl w-full max-w-2xl overflow-hidden">
          <div className="p-8 border-b border-slate-50 flex items-center justify-between bg-slate-50/50">
            <h2 className="text-2xl font-black text-slate-900 tracking-tighter flex items-center gap-3">
              <SafeIcon icon={FiBriefcase} className="text-blue-600" /> {project ? 'Edit Project' : 'New Project'}
            </h2>
            <button onClick={onClose} className="p-2 hover:bg-white rounded-xl transition-colors">
              <SafeIcon icon={FiX} className="text-slate-400" />
            </button>
          </div>
          <form onSubmit={handleSubmit} className="p-8 space-y-6 max-h-[75vh] overflow-y-auto custom-scrollbar">
            <input placeholder="Project Name" className="w-full text-xl font-bold p-4 bg-slate-50 rounded-2xl border border-slate-100 outline-none focus:ring-2 focus:ring-blue-500" value={formData.name} onChange={e => setFormData({ ...formData, name: e.target.value })} required />
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest pl-1">Client Company</label>
                <select className="w-full p-4 bg-slate-50 rounded-2xl border border-slate-100 outline-none font-bold appearance-none" value={formData.companyId} onChange={e => setFormData({ ...formData, companyId: e.target.value })}>
                  <option value="">No Company</option>
                  {companies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
              <div className="space-y-2">
                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest pl-1">Client Contact</label>
                <select className="w-full p-4 bg-slate-50 rounded-2xl border border-slate-100 outline-none font-bold appearance-none" value={formData.contactId} onChange={e => setFormData({ ...formData, contactId: e.target.value })}>
                  <option value="">No Individual Contact</option>
                  {contacts.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest pl-1">Billing Type</label>
                <select className="w-full p-4 bg-slate-50 rounded-2xl border border-slate-100 outline-none font-bold appearance-none" value={formData.billingType} onChange={e => setFormData({ ...formData, billingType: e.target.value })}>
                  <option>Fixed</option>
                  <option>Hourly</option>
                  <option>Retainer</option>
                </select>
              </div>
              <div className="space-y-2">
                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest pl-1">Status</label>
                <select className="w-full p-4 bg-slate-50 rounded-2xl border border-slate-100 outline-none font-bold appearance-none" value={formData.status} onChange={e => setFormData({ ...formData, status: e.target.value })}>
                  <option>Planning</option>
                  <option>In Progress</option>
                  <option>On Hold</option>
                  <option>Completed</option>
                </select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest pl-1">Budget Amount ($)</label>
                <div className="relative">
                  <SafeIcon icon={FiDollarSign} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-300" />
                  <input type="number" className="w-full pl-12 pr-4 py-4 bg-slate-50 rounded-2xl border border-slate-100 outline-none font-bold" value={formData.budget} onChange={e => setFormData({ ...formData, budget: e.target.value })} />
                </div>
              </div>
              <div className="space-y-2">
                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest pl-1">Budget Hours</label>
                <div className="relative">
                  <SafeIcon icon={FiClock} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-300" />
                  <input type="number" className="w-full pl-12 pr-4 py-4 bg-slate-50 rounded-2xl border border-slate-100 outline-none font-bold" value={formData.budgetHours} onChange={e => setFormData({ ...formData, budgetHours: e.target.value })} />
                </div>
              </div>
            </div>

            <button type="submit" className="w-full bg-blue-600 text-white py-4 rounded-2xl font-black shadow-xl shadow-blue-100 hover:bg-blue-700 transition-all">
              {project ? 'Update Project' : 'Create Project'}
            </button>
          </form>
        </motion.div>
      </div>
    </AnimatePresence>
  );
};

export default ProjectModal;